import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatEur, formatEurPerKg, formatKgDelta } from './formatters';
import type { AnimalEconomicStatus, AnimalEconomicStatusRaw, LifecyclePrediction } from './types';
import css from './DigitalTwinView.module.css';

interface EconomicForecastPanelProps {
  prediction: LifecyclePrediction;
}

const formatFactor = (factor: string): string => factor.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Normalise the C# numeric enum (0-4) or string to a canonical string status. */
const resolveStatus = (raw: AnimalEconomicStatusRaw | undefined): AnimalEconomicStatus => {
  if (raw === 0 || raw === 'Alive') return 'Alive';
  if (raw === 1 || raw === 'Sick') return 'Sick';
  if (raw === 2 || raw === 'Dead') return 'Dead';
  if (raw === 3 || raw === 'OtherIssue') return 'OtherIssue';
  if (raw === 4 || raw === 'Slaughtered') return 'Slaughtered';
  return 'Alive';
};

const statusCssClass: Record<AnimalEconomicStatus, string> = {
  Alive: css.statusBadgeAlive,
  Sick: css.statusBadgeSick,
  Dead: css.statusBadgeDead,
  OtherIssue: css.statusBadgeOtherIssue,
  Slaughtered: css.statusBadgeSlaughtered
};

const statusTranslationKey: Record<AnimalEconomicStatus, string> = {
  Alive: 'digitalTwin.economic.statusAlive',
  Sick: 'digitalTwin.economic.statusSick',
  Dead: 'digitalTwin.economic.statusDead',
  OtherIssue: 'digitalTwin.economic.statusOtherIssue',
  Slaughtered: 'digitalTwin.economic.statusSlaughtered'
};

/** Format a signed EUR value with the correct sign prefix and abs value. */
const formatSignedEur = (value: number): string => `${value < 0 ? '−' : '+'}${formatEur(Math.abs(value))}`;

export const EconomicForecastPanel: React.FC<EconomicForecastPanelProps> = ({ prediction }) => {
  const { t } = useTranslation();
  const ef = prediction.economicForecast;

  if (!ef) return null;

  const status = resolveStatus(ef.status);
  const isDead = status === 'Dead';
  const isSick = status === 'Sick';
  const isOtherIssue = status === 'OtherIssue';
  const isAlive = status === 'Alive';
  const isSlaughtered = status === 'Slaughtered';
  const isReliable = ef.isReliable !== false; // default true if missing
  const isStale = !isReliable;

  // Scenario detection — Slaughtered is now an explicit status (4)
  const hasSlaughterData = ef.actualValueEur != null;
  const showComparison = isSlaughtered || (isDead && hasSlaughterData);
  const isDeadNoSlaughter = isDead && !hasSlaughterData;
  const showAliveView = (isAlive || isSick || isOtherIssue) && !showComparison;

  // Common flags
  const hasLoss = ef.lossForecastEur !== 0;
  const hasBioGap = ef.biologicalGapKg !== 0 && isReliable;
  const hasTotalLoss = ef.totalLossEur != null && ef.totalLossEur !== 0;
  const showAttribution = ef.attribution && ef.attribution.length > 0 && !showComparison;

  const rootClassName = `${css.section} ${isStale ? css.sectionStale : ''}`;

  return (
    <section className={rootClassName} aria-labelledby="dt-section-economic">
      {/* ── Header ── */}
      <div className={css.sectionHeader}>
        <div className={css.sectionHeaderLeft}>
          <h3 id="dt-section-economic" className={css.sectionTitle}>
            {t('digitalTwin.section.economic')}
          </h3>
          <span className={`${css.statusBadge} ${statusCssClass[status]}`}>{t(statusTranslationKey[status])}</span>
          {isStale && (
            <span className={css.staleBadge} title={t('digitalTwin.economic.staleTooltip')}>
              {t('digitalTwin.economic.stale')}
            </span>
          )}
        </div>
        <p className={css.sectionHint}>{t('digitalTwin.section.economicHint')}</p>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          SCENARIO 1 — Alive / Sick (no slaughter data)
         ══════════════════════════════════════════════════════════════ */}
      {showAliveView && (
        <>
          <div className={css.kpiRow}>
            {/* Predicted value */}
            <article className={`${css.kpiCard} ${css.kpiCardEconomicValue}`}>
              <h3 className={css.kpiLabel}>{t('digitalTwin.economic.predictedValue')}</h3>
              <p className={`${css.kpiValue} ${css.kpiValueTeal}`}>{formatEur(ef.predictedValueEur)}</p>
              <p className={css.kpiFootnote}>{t('digitalTwin.economic.atCurrentTrajectory')}</p>
            </article>

            {/* Revenue at risk */}
            <article className={`${css.kpiCard} ${hasLoss ? css.kpiCardAccentLoss : css.kpiCardAccentProfit}`}>
              <h3 className={css.kpiLabel}>{t('digitalTwin.economic.revenueAtRisk')}</h3>
              {hasLoss ? (
                <p className={`${css.kpiValue} ${css.kpiValueLoss}`}>{formatSignedEur(ef.lossForecastEur)}</p>
              ) : (
                <p className={`${css.kpiValue} ${css.kpiValueProfit}`}>{t('digitalTwin.economic.onTrack')}</p>
              )}
              <p className={css.kpiFootnote}>
                {hasBioGap
                  ? t('digitalTwin.economic.biologicalGap', { gap: ef.biologicalGapKg.toFixed(1) })
                  : t('digitalTwin.economic.onTargetWeight')}
              </p>
            </article>

            {/* Price per kg */}
            <article className={`${css.kpiCard} ${css.kpiCardAccentOperational}`}>
              <h3 className={css.kpiLabel}>{t('digitalTwin.economic.pricePerKg')}</h3>
              <p className={css.kpiValue}>{formatEurPerKg(ef.pricePerKgEur)}</p>
              <p className={css.kpiFootnote}>{t('digitalTwin.economic.marketPriceUsed')}</p>
            </article>
          </div>

          {/* OtherIssue — loss banner using lossForecastEur (totalLossEur no longer set) */}
          {isOtherIssue && hasLoss && (
            <div className={css.totalLossBanner}>
              <div className={css.totalLossLabel}>{t('digitalTwin.economic.partialWriteOff')}</div>
              <div className={css.totalLossValue}>{formatSignedEur(ef.lossForecastEur)}</div>
              <div className={css.totalLossHint}>{t('digitalTwin.economic.otherIssueApplied')}</div>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════
          SCENARIO 2 — Slaughtered (or dead with slaughter data)
          Post-mortem: expected vs. reality
         ══════════════════════════════════════════════════════════════ */}
      {showComparison && (
        <>
          {/* Delta headline */}
          <div className={css.comparisonDeltaBanner}>
            <div className={css.comparisonDeltaLabel}>{t('digitalTwin.economic.deltaLabel')}</div>
            <div
              className={`${css.comparisonDeltaValue} ${
                (ef.deltaEur ?? 0) >= 0 ? css.comparisonDeltaPositive : css.comparisonDeltaNegative
              }`}
            >
              {ef.deltaEur != null ? formatSignedEur(ef.deltaEur) : '—'}
            </div>
            {isStale && <div className={css.comparisonDeltaHint}>{t('digitalTwin.economic.slaughterLate')}</div>}
          </div>

          <div className={css.kpiRow}>
            {/* Expected */}
            <article className={`${css.kpiCard} ${css.kpiCardEconomicValue}`}>
              <h3 className={css.kpiLabel}>{t('digitalTwin.economic.expected')}</h3>
              <p className={`${css.kpiValue} ${css.kpiValueTeal}`}>{formatEur(ef.predictedValueEur)}</p>
              <p className={css.kpiFootnote}>{t('digitalTwin.economic.predictedAtTarget')}</p>
            </article>

            {/* Actual */}
            <article className={`${css.kpiCard} ${css.kpiCardAccentOperational}`}>
              <h3 className={css.kpiLabel}>{t('digitalTwin.economic.actual')}</h3>
              <p className={css.kpiValue}>{formatEur(ef.actualValueEur!)}</p>
              {ef.deltaKg != null && (
                <p className={css.kpiFootnote}>
                  {t('digitalTwin.economic.weightDelta', { delta: ef.deltaKg.toFixed(1) })}
                </p>
              )}
            </article>

            {/* Price per kg */}
            <article className={`${css.kpiCard} ${css.kpiCardAccentOperational}`}>
              <h3 className={css.kpiLabel}>{t('digitalTwin.economic.pricePerKg')}</h3>
              <p className={css.kpiValue}>{formatEurPerKg(ef.pricePerKgEur)}</p>
              <p className={css.kpiFootnote}>{t('digitalTwin.economic.marketPriceUsed')}</p>
            </article>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════
          SCENARIO 3 — Dead without slaughter data (complete write-off)
         ══════════════════════════════════════════════════════════════ */}
      {isDeadNoSlaughter && (
        <>
          {hasTotalLoss && (
            <div className={css.totalLossBanner}>
              <div className={css.totalLossLabel}>{t('digitalTwin.economic.totalWriteOff')}</div>
              <div className={css.totalLossValue}>−{formatEur(ef.totalLossEur!)}</div>
              <div className={css.totalLossHint}>
                {hasBioGap
                  ? t('digitalTwin.economic.biologicalGap', { gap: ef.biologicalGapKg.toFixed(1) })
                  : t('digitalTwin.economic.onTargetWeight')}
              </div>
            </div>
          )}

          <div className={css.kpiRow}>
            <article className={`${css.kpiCard} ${css.kpiCardAccentLoss}`}>
              <h3 className={css.kpiLabel}>{t('digitalTwin.economic.totalLossLabel')}</h3>
              <p className={`${css.kpiValue} ${css.kpiValueLoss}`}>
                {hasTotalLoss ? `−${formatEur(ef.totalLossEur!)}` : '—'}
              </p>
              <p className={css.kpiFootnote}>{t('digitalTwin.economic.animalDeceased')}</p>
            </article>

            <article className={`${css.kpiCard} ${css.kpiCardAccentOperational}`}>
              <h3 className={css.kpiLabel}>{t('digitalTwin.economic.pricePerKg')}</h3>
              <p className={css.kpiValue}>{formatEurPerKg(ef.pricePerKgEur)}</p>
              <p className={css.kpiFootnote}>{t('digitalTwin.economic.marketPriceUsed')}</p>
            </article>
          </div>
        </>
      )}

      {/* ── Attribution table (only for alive/sick/otherIssue without slaughter data) ── */}
      {showAttribution && (
        <div className={css.attributionPanel}>
          <p className={css.attributionTitle}>{t('digitalTwin.economic.attributionTitle')}</p>
          <table className={css.attributionTable}>
            <thead>
              <tr>
                <th className={css.attributionTh}>{t('digitalTwin.economic.factor')}</th>
                <th className={`${css.attributionTh} ${css.attributionThRight}`}>
                  {t('digitalTwin.economic.weightImpact')}
                </th>
                <th className={`${css.attributionTh} ${css.attributionThRight}`}>
                  {t('digitalTwin.economic.eurImpact')}
                </th>
              </tr>
            </thead>
            <tbody>
              {ef.attribution.map((item, i) => {
                const isAntibioticDelay = item.factor === 'AntibioticDelay';
                return (
                  <tr key={i} className={css.attributionRow}>
                    <td className={css.attributionFactor}>{formatFactor(item.factor)}</td>
                    <td className={`${css.attributionCell} ${css.attributionCellRight}`}>
                      {/* AntibioticDelay: weightImpactKg is always 0 — hide it */}
                      {isAntibioticDelay ? '—' : item.weightImpactKg !== 0 ? formatKgDelta(item.weightImpactKg) : '—'}
                    </td>
                    <td
                      className={`${css.attributionCell} ${css.attributionCellRight} ${
                        item.eurImpact < 0 ? css.attributionNegative : item.eurImpact > 0 ? css.attributionPositive : ''
                      }`}
                    >
                      {item.eurImpact !== 0 ? formatSignedEur(item.eurImpact) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
